import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import { useTranslation } from "react-i18next";
import { useCookies } from "react-cookie";
import { toast } from "react-toastify";
import { UserContext } from "../../context/UserContext";
import BillingRecipientService, {
  BillingRecipient,
  BillingRecipientCreateRequest,
  BillingNotificationType,
} from "../../services/BillingRecipientService";
import * as CONSTANTS from "../../utils/constants/constants";

const NOTIFICATION_OPTIONS: BillingNotificationType[] = [
  "monthly_summary",
  "weekly_summary",
  "billing_alert",
  "payment_reminder",
  "audit_report",
];

interface RecipientFormState extends BillingRecipientCreateRequest {}

const emptyForm: RecipientFormState = {
  clinic_id: 0,
  email: "",
  name: "",
  title: "",
  notification_types: ["monthly_summary"],
  is_active: true,
  send_on_weekends: false,
};

export const BillingRecipientsPage: React.FC = () => {
  const { t } = useTranslation();
  const [cookies] = useCookies(["access_token"]);
  const accessToken = cookies.access_token;
  const context = useContext(UserContext);
  const [user] = useState(context?.user);
  const organizationId = user?.organization || "";

  const [recipients, setRecipients] = useState<BillingRecipient[]>([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<RecipientFormState>(emptyForm);
  const [sendingSummary, setSendingSummary] = useState(false);

  const [clinics, setClinics] = useState<any[]>([]);
  const [clinicsLoading, setClinicsLoading] = useState(false);
  const loadRecipients = async () => {
    if (!accessToken) return;
    if (!user) return; // wait for user context
    setLoading(true);
    try {
      const params = organizationId ? { organization_id: organizationId } : {};
      const data: any = await BillingRecipientService.listRecipients(accessToken, params);
      console.log("[BillingRecipientsPage] raw recipients data", data);
      const list = Array.isArray(data?.results)
        ? data.results
        : Array.isArray(data?.recipients)
        ? data.recipients
        : Array.isArray(data)
        ? data
        : [];
      console.log("[BillingRecipientsPage] normalized recipients list length", list.length);
      setRecipients(list || []);
    } catch (err: any) {
      console.error("Error loading recipients", err);
      toast.error(err?.message || t("Failed to load recipients"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRecipients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, organizationId, user]);

  // Load clinics for dropdown (runs once on mount / token change)
  useEffect(() => {
    const fetchClinics = async () => {
      if (!accessToken) return;
      setClinicsLoading(true);
      try {
        const endpoint = CONSTANTS.HOST + "api/v1/cohort/clinics/";
        const resp = await fetch(endpoint, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        });
        const contentType = resp.headers.get("content-type") || "";
        if (!contentType.includes("application/json")) {
          const text = await resp.text();
          throw new Error("Unexpected response format: " + text.slice(0, 60));
        }
        const data = await resp.json();
        const arr = Array.isArray(data) ? data : data.results || [];
        setClinics(arr);
      } catch (e: any) {
        console.error("Failed to load clinics", e);
        toast.error(e?.message || t("Failed to load clinics"));
      } finally {
        setClinicsLoading(false);
      }
    };
    fetchClinics();
  }, [accessToken, t]);

  const handleOpenCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setOpenDialog(true);
  };

  const handleEdit = (r: BillingRecipient) => {
    setEditingId(r.id);
    setForm({
      clinic_id: r.clinic.id,
      email: r.email,
      name: r.name,
      title: r.title || "",
      notification_types: r.notification_types,
      is_active: r.is_active,
      send_on_weekends: r.send_on_weekends,
    });
    setOpenDialog(true);
  };

  const handleDelete = async (id: number) => {
    if (!accessToken) return;
    if (!window.confirm(t("Are you sure you want to delete this recipient?"))) return;
    try {
      await BillingRecipientService.deleteRecipient(accessToken, id);
  toast.success(String(t("Recipient deleted")));
      setRecipients((prev) => prev.filter((r) => r.id !== id));
    } catch (err: any) {
      toast.error(err?.message || t("Delete failed"));
    }
  };

  const handleSave = async () => {
    if (!accessToken) return;
    try {
      if (!form.clinic_id) {
  toast.error(String(t("Clinic ID is required")));
        return;
      }
      if (editingId) {
        const updated = await BillingRecipientService.updateRecipient(accessToken, editingId, form);
        setRecipients((prev) => prev.map((r) => (r.id === editingId ? updated : r)));
  toast.success(String(t("Recipient updated")));
      } else {
        const created = await BillingRecipientService.createRecipient(accessToken, form);
        setRecipients((prev) => [...prev, created]);
  toast.success(String(t("Recipient created")));
      }
      setOpenDialog(false);
    } catch (err: any) {
      toast.error(err?.message || t("Save failed"));
    }
  };

  const handleSendMonthlySummary = async () => {
    if (!accessToken) return;
    const clinicIds = Array.from(new Set(recipients.map((r) => r.clinic.id)));
    if (clinicIds.length !== 1) {
  toast.info(String(t("Select a single clinic's recipients to send summary (filter not implemented yet)")));
      return;
    }
    const clinicId = clinicIds[0];
    const billingMonth = new Date();
    const monthStr = `${billingMonth.getFullYear()}-${String(billingMonth.getMonth() + 1).padStart(2, "0")}`;
    setSendingSummary(true);
    try {
      const response = await BillingRecipientService.sendBillingSummary(accessToken, {
        clinic_id: clinicId,
        billing_month: monthStr,
        month: monthStr,
        notification_type: "monthly_summary",
        recipient_emails: recipients.map((r) => r.email),
        organization_id: organizationId,
      });
      const sent = response?.details?.emails_sent ?? response?.details?.total_recipients ?? "?";
      toast.success(`${t("Summary sent")}: ${sent}`);
    } catch (err: any) {
      toast.error(err?.message || t("Send failed"));
    } finally {
      setSendingSummary(false);
    }
  };

  return (
    <Box sx={{ padding: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={6}>
                <h2>{t("Billing Recipients")}</h2>
              </Grid>
              <Grid item xs={6} style={{ textAlign: "right" }}>
                <Button variant="contained" color="primary" onClick={handleOpenCreate} sx={{ mr: 1 }}>
                  {t("Add Recipient")}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={loadRecipients}
                  sx={{ mr: 1 }}
                  disabled={loading}
                >
                  {loading ? t("Loading...") : t("Refresh")}
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<SendIcon />}
                  onClick={handleSendMonthlySummary}
                  disabled={sendingSummary || recipients.length === 0}
                >
                  {sendingSummary ? t("Sending...") : t("Send Monthly Summary")}
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            {loading ? (
              <Box sx={{ textAlign: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : recipients.length === 0 ? (
              <Box sx={{ textAlign: "center", p: 4 }}>{t("No recipients found")}</Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("ID")}</TableCell>
                      <TableCell>{t("Clinic")}</TableCell>
                      <TableCell>{t("Email")}</TableCell>
                      <TableCell>{t("Name")}</TableCell>
                      <TableCell>{t("Title")}</TableCell>
                      <TableCell>{t("Notifications")}</TableCell>
                      <TableCell>{t("Active")}</TableCell>
                      <TableCell>{t("Weekends")}</TableCell>
                      <TableCell>{t("Actions")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recipients.map((r) => (
                      <TableRow key={r.id} hover>
                        <TableCell>{r.id}</TableCell>
                        <TableCell>{r.clinic?.name || r.clinic.id}</TableCell>
                        <TableCell>{r.email}</TableCell>
                        <TableCell>{r.name}</TableCell>
                        <TableCell>{r.title}</TableCell>
                        <TableCell>
                          {r.notification_types.map((n) => (
                            <Chip key={n} label={n} size="small" sx={{ mr: 0.5 }} />
                          ))}
                        </TableCell>
                        <TableCell>{r.is_active ? t("Yes") : t("No")}</TableCell>
                        <TableCell>{r.send_on_weekends ? t("Yes") : t("No")}</TableCell>
                        <TableCell>
                          <Tooltip title={t("Edit") as string}>
                            <IconButton size="small" onClick={() => handleEdit(r)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t("Delete") as string}>
                            <IconButton size="small" onClick={() => handleDelete(r.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingId ? t("Edit Recipient") : t("Add Recipient")}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
            <FormControl fullWidth required>
              <InputLabel>{t("Clinic")}</InputLabel>
              <Select
                value={form.clinic_id ? String(form.clinic_id) : ""}
                label={t("Clinic")}
                onChange={(e) => setForm({ ...form, clinic_id: parseInt(String(e.target.value as string), 10) })}
                disabled={clinicsLoading}
              >
                {clinics.map((c: any) => {
                  const idVal = c.clinic_id || c.id;
                  return (
                    <MenuItem key={idVal} value={String(idVal)}>
                      {c.name || `Clinic ${idVal}`}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <TextField
              label={t("Email")}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label={t("Name")}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label={t("Title")}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>{t("Notification Types")}</InputLabel>
              <Select
                multiple
                value={form.notification_types}
                label={t("Notification Types")}
                onChange={(e) =>
                  setForm({ ...form, notification_types: e.target.value as BillingNotificationType[] })
                }
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {(selected as string[]).map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {NOTIFICATION_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
              }
              label={t("Active")}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.send_on_weekends}
                  onChange={(e) => setForm({ ...form, send_on_weekends: e.target.checked })}
                />
              }
              label={t("Send on Weekends")}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>{t("Cancel")}</Button>
          <Button variant="contained" onClick={handleSave}>
            {t("Save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BillingRecipientsPage;
